import React, {
    useCallback,
    useMemo,
    useRef,
} from 'react';
import {
    formatAmountShortText,
    formatPercentCss,
    formatPercentExt,
} from '@frontend/jetlend-core/src/formatters/formatUtils';
import { buildRange } from '@frontend/jetlend-core/src/utils/arrayUtils';
import { buildClassNames } from '../../utils/classNameUtils';
import Tooltip from '../TooltipIcon/Tooltip';
import styles from './progressBar.module.scss';

export type ProgressBarType = 'default' | 'danger' | 'warning' | 'success' | 'success-opaque' | 'primary' | 'light' | 'info' | 'waiting' | 'colorful' | 'mobile';

export type ProgressBarSize = 'narrow' | 'default' | 'large' | 'xlarge' | 'small' | 'thin';

export interface ProgressBarProps {
    progress: number;
    hideLabel?: boolean;
    label?: string;
    type?: ProgressBarType;
    size?: ProgressBarSize;
    useStripes?: boolean;
    withBorder?: boolean;
    hint?: JSX.Element|React.ReactNode;
    minValue?: number;
    maxValue?: number;
    dots?: boolean;
    /**
     * Темный серый цвет фона прогресс бара
     */
    darkGrey?: boolean;
}

const DOTS_COUNT = 4;
const DOT_POSITION_STEP = 1 / (DOTS_COUNT + 1);
const HIGHLIGHTING_OFFSET = 0.005;

const ProgressBar: React.FC<ProgressBarProps> = ({
    progress,
    hideLabel,
    label,
    type,
    size,
    useStripes,
    withBorder,
    hint,
    minValue,
    maxValue,
    dots,
    darkGrey,
}) => {
    const getType = () => {
        if (type) {
            return type;
        } if (progress < 0.2) {
            return 'danger';
        } if (progress < 0.75) {
            return 'warning';
        }
        return 'success';
    };

    const progressBarRef = useRef();

    const containerClassName = buildClassNames(styles, [
        'container',
        `container--${size}`,
        `container--${getType()}`,
        progress < 0.52 && 'container--default-color',
        useStripes && `container-gradient--${getType()}`,
        withBorder && 'container--with-border',
        darkGrey && 'container--grey',
    ]);

    const barClassName = buildClassNames(styles, [
        'bar',
        useStripes && 'bar--stripes',
    ]);

    const barInlineStyles = useMemo(() => ({
        width: formatPercentCss(progress),
        borderRadius: 12,
    }), [ progress ]);

    const labelClassName = buildClassNames(styles, [
        'label',
    ]);

    /** Расчёт отступа для скругления вспомогательной линии, в зависимости от размера size
     * Равно радиусу скругления линии при каждом размере size
     * Отступ нужен для того, чтобы supportProgress перекрывал Progress в зоне скругления
    */
    const getOffset = useCallback(() => {
        if (size === 'xlarge') {
            return 12;
        } if (size === 'large') {
            return 9;
        }
        return 6;
    }, [size]);

    /** Инлайн-стили для вспомогательной линии, которая занимает правую часть ProgressBar
     * progress + supportProgress = 100%
    */
    const supportBarInlineStyles = useMemo(() => {
        const offset = getOffset();
        if (progress === 1) {
            return;
        }
        /** Рассчитываем позицию вспомогательной линии, с учетом progress и скругления ProgressBar */
        return {
            /** Значение ширины supportProgress = 100% - progress + offset (размер скругления)  */
            width: `calc(${formatPercentCss(1-progress)} + ${offset}px)`,
            /** Горизонтальное позиционирование supportProgress = progress - offset (размер скругления)  */
            left: `calc(${formatPercentCss(progress)} - ${offset}px)`,
        };
    }, [getOffset, progress]);

    const supportBarClassName = buildClassNames(styles, [
        'support-bar',
    ]);

    return (
        <>
            <div ref={progressBarRef} className={containerClassName}>
                <div className={supportBarClassName} style={supportBarInlineStyles} />
                <div className={barClassName} style={barInlineStyles} />
                {dots && buildRange(1, DOTS_COUNT).map(index => (
                    <div
                        key={index}
                        className={styles['dot']}
                        style={{
                            left: formatPercentCss(index * DOT_POSITION_STEP),
                            // Use highlighting offset to inverse dot color on bar edge
                            backgroundColor: progress > (index * DOT_POSITION_STEP - HIGHLIGHTING_OFFSET)
                                ? '#fff'
                                : undefined,
                        }}
                    />
                ))
                }
                {(!hideLabel && type !== 'mobile') && (
                    <div className={labelClassName}>
                        {label} {formatPercentExt(progress, { fractionDigits: 0 })}
                    </div>
                )}
            </div>
            <Tooltip title={hint} disableWrapper reference={progressBarRef} />
            {(minValue || maxValue) && (
                <div className={styles['range-labels']}>
                    <div>{formatAmountShortText(minValue)}</div>
                    <div>{formatAmountShortText(maxValue)}</div>
                </div>
            )}
        </>
    );
};

export default ProgressBar;